import { useRef } from 'react';

import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import useFaqSubmit from '@/components/atoms/FileUpload/useFaqSubmit';
import useDatasources from '@/hooks/useDatasources';
import { selectAccountId, selectDatasourceId } from '@/redux/session/selectors';
import { CollectionDocument, DatasourceType } from '@/shared/types/collections';
import { FILES_BASE_URL } from '@/util/constants';

import { MAX_TXT_PREVIEW_LENGTH, ROOT_DATASOURCE_TYPES } from '../constants';
import { useAPIDocuments } from './useAPIDocuments';
import { DatasourceDocument } from '../types';
import { computeTxtPreview } from '../utils/preview';
import { checkDocumentNameAvailability } from '../validator';

type UseUploadProps = { collectionId: string };

function isTxtFile(file: File): boolean {
  return file.type === 'text/plain' || file.name.toLowerCase().endsWith('.txt');
}

async function computeTxtPreviewSafe(
  file: File,
  maxLength: number
): Promise<{ isTxt: boolean; preview: string }> {
  const isTxt = isTxtFile(file);
  if (!isTxt) {
    return { isTxt, preview: '' };
  }
  try {
    const content = await file.text();
    return { isTxt, preview: computeTxtPreview(content, maxLength) };
  } catch {
    return { isTxt, preview: '' };
  }
}

export const useUpload = ({ collectionId }: UseUploadProps) => {
  // Redux
  const accountId = useSelector(selectAccountId);
  const datasourceId = useSelector(selectDatasourceId);

  // Hooks
  const { createDatasourceAsync } = useDatasources(collectionId);
  const { createDocument, updateDocument } = useAPIDocuments({
    collection_id: collectionId,
    datasource_id: datasourceId,
  });
  const { uploadFileToUrl } = useFaqSubmit();

  /**
   * Saves a document to the specified datasource within a collection.
   *
   * @param file - The file to upload
   * @param datasourceId - The ID of the datasource to save the document to
   * @param collection_id - Optional collection ID. If not provided, falls back to the hook's collectionId
   */
  const saveDocument = async (file: File, datasourceId: string, collection_id?: string) => {
    // Check for duplicate name before uploading
    const nameCheckResult = await checkDocumentNameAvailability({
      collectionId: collection_id ?? collectionId,
      datasourceId,
      name: file.name,
    });

    if (!nameCheckResult.available) {
      throw new Error(
        nameCheckResult.message || `A file with the name "${file.name}" already exists`
      );
    }

    const response = await uploadFileToUrl(
      file,
      `${FILES_BASE_URL}/${accountId}/${collection_id ?? collectionId}/${datasourceId}`
    );
    const fileExtension = file.name.split('.').pop();

    const { isTxt, preview } = await computeTxtPreviewSafe(file, MAX_TXT_PREVIEW_LENGTH);
    createDocument({
      datasource_id: datasourceId,
      type: fileExtension,
      name: file.name,
      file_url: response.url,
      // Add preview to metadata only if isTxt is true
      ...(isTxt ? { metadata: { preview } } : {}),
    });
  };

  // Use refs to persist the root datasource ID across multiple uploads
  const rootDatasourceIdRef = useRef<string | null>(null);
  const handleUploadDocumentToRootDatasource = async (
    file: File,
    datasourceId: string,
    collection_id?: string
  ) => {
    // Get the root datasource ID or create a new one if it doesn't exist
    let targetDatasourceId = datasourceId ?? rootDatasourceIdRef.current;

    if (!targetDatasourceId) {
      // Create the root datasource for documents
      const { datasource_id } = await createDatasourceAsync(
        ROOT_DATASOURCE_TYPES[DatasourceType.FILES]
      );
      targetDatasourceId = datasource_id;
    }

    rootDatasourceIdRef.current = targetDatasourceId;

    return saveDocument(file, targetDatasourceId, collection_id);
  };

  const handleUploadDocumentToFolderDatasource = async (file: File, datasourceId: string) => {
    return saveDocument(file, datasourceId);
  };

  const handleReplaceDocument = async ({
    file,
    document,
  }: {
    file: File;
    document: CollectionDocument | DatasourceDocument;
  }) => {
    const { document_id, datasource_id } = document;
    const originalName = document?.file_url.split('/').pop();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('originalName', originalName);

    // Replace the file in the datasource
    const resp = await uploadFileToUrl(
      file,
      `${FILES_BASE_URL}/${accountId}/${collectionId}/${datasource_id}`
    );

    // Update the document in the database
    const { isTxt, preview } = await computeTxtPreviewSafe(file, MAX_TXT_PREVIEW_LENGTH);
    updateDocument(
      {
        document_id,
        name: resp.name,
        type: resp.type,
        file_url: resp.url,
        ...(isTxt ? { metadata: { ...document?.metadata, preview } } : {}),
      },
      {
        onSuccess: () => {
          toast.success('Document replaced successfully');
        },
      }
    );
  };
  return {
    handleReplaceDocument,
    handleUploadDocumentToRootDatasource,
    handleUploadDocumentToFolderDatasource,
  };
};
